import { Button } from '@apollo/orbit';
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useRouteMatch } from 'react-router-dom';

import { useShowToast } from 'src/components/toast/Toast';

import {
  proposalChangesRouteConfig,
  proposalEditorRouteConfig,
} from '../routes';

export const useRecentPublishViewChangesToast = () => {
  const [currentToastId, setCurrentToastId] = useState<string>();
  const { showToasts, hideToasts } = useShowToast();
  const isChangesPage = !!useRouteMatch(proposalChangesRouteConfig.definition);

  const location = useLocation();

  // Hide toast after 30 seconds always
  React.useEffect(() => {
    if (currentToastId) {
      const timeout = window.setTimeout(
        () => hideToasts(currentToastId),
        30 * 1000,
      );
      return () => window.clearTimeout(timeout);
    }
  }, [hideToasts, currentToastId]);

  const showPublishRevisionToast = useCallback(
    (isSuccessful: boolean, errorMessage: string | undefined) => {
      // Don't show this toast on changes page unless the publish was unsuccessful
      if (!currentToastId && (!isSuccessful || !isChangesPage)) {
        const toastIds = showToasts({
          theme: 'dark',
          heading: isSuccessful
            ? `Revision saved successfully!`
            : `Revision not saved!`,
          level: isSuccessful ? 'success' : 'error',
          message: isSuccessful
            ? `To dive into your revision, head over to the Changes page.`
            : errorMessage || '',
          onDismiss: () => {
            setCurrentToastId(undefined);
          },
          actions: (closeModal) => {
            return isSuccessful ? (
              <Button
                // 🚨 Do not delete or change this data-testid / this button without updating the Proposals Datadog Browser test
                // TODO(Maya) add link to synthetics test
                data-testid="proposals_toast_go_to_changes_button"
                as={Link}
                to={proposalChangesRouteConfig.locationFrom({
                  location,
                  fromRouteConfig: proposalEditorRouteConfig,
                })}
                onClick={closeModal}
              >
                Go to Changes
              </Button>
            ) : (
              <Button
                onClick={() => {
                  window.location.reload();
                }}
              >
                Refresh
              </Button>
            );
          },
        });
        setCurrentToastId(toastIds?.[0]);
      }
    },
    [currentToastId, isChangesPage, showToasts, location],
  );

  useEffect(() => {
    return () => {
      if (currentToastId) {
        hideToasts(currentToastId);
      }
    };
  }, [hideToasts, currentToastId, location.pathname]);

  return {
    hidePublishRevisionToast: useCallback(() => {
      if (currentToastId) {
        hideToasts(currentToastId);
      }
    }, [currentToastId, hideToasts]),
    showPublishRevisionToast,
  };
};
